import {Boundaries} from "./components/Boundaries.tsx";
import type {SidebarValues} from "./App.tsx";
import {Canvas, useThree} from "@react-three/fiber";
import Sources from "./Sources.tsx";
import {useEffect, useState} from "react";
import {Grid} from "./components/Grid.tsx";
import {Plane} from "./components/Plane.tsx";
import {BackingLight} from "./components/BackingLight.tsx";

export type Dimensions = { width: number; height: number; depth: number };
export type TouchingBoundary = { back: boolean, left: boolean, right: boolean, top: boolean, bottom: boolean, yPinned: number | null };
export const TouchingNone:TouchingBoundary = {back: false, left: false, right: false, top: false, bottom: false, yPinned: null };

type SceneProps = { setValues: SidebarValues };

const depth = 25;
const sphereDiameter = 1.5;

/**
 * Inner scene content that uses useThree to access viewport dimensions.
 */
function SceneContent({setValues}: SceneProps) {

    const {viewport, invalidate} = useThree();
    const width = viewport.width;
    const height = viewport.height;

    // Re-render when viewport dimensions change (e.g. fullscreen toggle)
    useEffect(() => {
        invalidate();
    }, [width, height, invalidate]);

    const dimensions = {width, height, depth, sphereDiameter};
    const sceneWidth = width + sphereDiameter;
    const sceneHeight = height + sphereDiameter;
    const sceneDepth = depth + sphereDiameter / 2;
    const sceneDimensions = {width: sceneWidth, height: sceneHeight, depth: sceneDepth};

    const [touching, setTouching] = useState(TouchingNone);

    return (<>
        <ambientLight intensity={0.2}/>
        <directionalLight intensity={0.5} position={[1, 2, 3]}/>
        <BackingLight intensity={1} scene={sceneDimensions}/>
        <Boundaries intensity={0.01} scene={sceneDimensions} touched={touching}/>
        <Grid dimensions={sceneDimensions} lineWidth={1} active={true}/>
        <Plane dimensions={sceneDimensions} yPosition={touching.yPinned} color={"#ddd"}/>

        <Sources dimensions={dimensions} setValues={setValues}
                 setTouchingBoundary={setTouching}/>
    </>);
}

/**
 * The 3D scene.
 * It contains the audio sources and the boundaries.
 * @param setValues callback to set values in the sidebar
 */
export default function Scene({setValues}: SceneProps) {

    return (<div className="scene-area">
        <Canvas fallback={<div>No WebGL supported! 3D audio scene is not working - sorry.</div>}
                frameloop="demand" orthographic={false}
                camera={{  position: [0, 0, 25], fov: 4.5, near: 23, far: 55}}
                style={{
                    background: '#1a1a1a', width: '100%', height: '100%',
                    touchAction: 'none', outline: "none"
                }}
        >
            <SceneContent setValues={setValues}/>
        </Canvas>
    </div>)
}
